import React from 'react';
import './FrameQC.css';
import { CheckCircle, AlertTriangle, Ban, Film } from 'lucide-react';

/**
 * FrameQC - shows blank/black-frame check results for a video, with optional
 * compare-mode diff highlighting against the other side's result.
 *
 * @param {object|null} result   - output of analyzeBlankFrames()
 * @param {boolean} loading      - analysis in progress
 * @param {object|null} compareWith - the other side's result (compare tool)
 */
function FrameQC({ result, loading, compareWith }) {
    if (loading) {
        return (
            <div className="frame-qc">
                <div className="frame-qc-row frame-qc-loading">
                    <div className="spinner spinner-sm"></div>
                    <span>Checking frames…</span>
                </div>
            </div>
        );
    }

    if (!result) return null;

    // Codec the browser can't decode — never report a (false) all-black result.
    if (!result.decodable) {
        return (
            <div className="frame-qc">
                <div className="frame-qc-row frame-qc-na">
                    <Ban size={13} className="frame-qc-icon" />
                    <span className="frame-qc-text">Frame check unavailable — codec not decodable in browser</span>
                </div>
            </div>
        );
    }

    if (result.wholeClip) {
        return (
            <div className="frame-qc">
                <div className="frame-qc-row frame-qc-warn">
                    <AlertTriangle size={13} className="frame-qc-icon" />
                    <span className="frame-qc-text">Entire clip appears blank/black</span>
                </div>
            </div>
        );
    }

    const headDiff = countOf(result.head) !== countOf(compareWith?.head);
    const tailDiff = countOf(result.tail) !== countOf(compareWith?.tail);
    const hasCompare = !!compareWith && compareWith.decodable;

    const rows = [];
    rows.push(endRow('head', result.head, hasCompare && headDiff));
    rows.push(endRow('tail', result.tail, hasCompare && tailDiff));

    return (
        <div className="frame-qc">
            <div className="frame-qc-header">
                <Film size={12} className="frame-qc-header-icon" />
                <span>Frame QC</span>
            </div>
            {rows}
        </div>
    );
}

function countOf(end) {
    return end ? end.count : 0;
}

function typeWord(type) {
    if (type === 'black') return 'black';
    if (type === 'flat') return 'blank';
    return 'black/blank';
}

function endRow(which, end, isDiff) {
    const position = which === 'head' ? 'leading' : 'trailing';
    const clean = !end;

    return (
        <div
            key={which}
            className={`frame-qc-row ${clean ? 'frame-qc-ok' : 'frame-qc-warn'} ${isDiff ? 'frame-qc-diff' : ''}`}
        >
            {clean ? (
                <CheckCircle size={13} className="frame-qc-icon" />
            ) : (
                <AlertTriangle size={13} className="frame-qc-icon" />
            )}
            <span className="frame-qc-label">{which === 'head' ? 'Head' : 'Tail'}</span>
            <span className="frame-qc-text">
                {clean
                    ? 'clean'
                    : `${end.count} ${position} ${typeWord(end.type)} ${end.count === 1 ? 'frame' : 'frames'} (${end.seconds}s)`}
            </span>
            {isDiff && <span className="frame-qc-diff-dot">●</span>}
        </div>
    );
}

export default FrameQC;
